import { useState, useEffect } from "react";
import { Slider } from "@/components/ui/slider";
import { SHAPE_TYPES, DEFAULT_VIDEO_SHAPE } from "@/lib/shapes";
import { isFeatureEnabled } from "@/lib/features";
import { absUrl } from "@/lib/timeline";
import { api } from "@/lib/api";
import { Search } from "lucide-react";

const GRADIENTS = [
  { from: "#0f2027", to: "#2c5364", label: "Ocean" },
  { from: "#8B5CF6", to: "#00F0FF", label: "Neon" },
  { from: "#f12711", to: "#f5af19", label: "Sunset" },
  { from: "#000428", to: "#004e92", label: "Midnight" },
  { from: "#134E5E", to: "#71B280", label: "Field" },
  { from: "#41295a", to: "#2F0743", label: "Grape" },
  { from: "#ee0979", to: "#ff6a00", label: "Blaze" },
  { from: "#1a1a2e", to: "#0f0f1a", label: "Studio" },
];
const SOLIDS = ["#000000", "#0A0A0A", "#1a1a2e", "#0d3b66", "#1b4332", "#7b1e1e", "#3a0ca3", "#ffffff", "#111827", "#2c3e50"];

function Field({ label, val, children }) {
  return (
    <div className="mb-2">
      <div className="mb-1 flex justify-between text-[11px] text-[#A1A1AA]"><span>{label}</span><span className="tabular-nums text-white">{val}</span></div>
      {children}
    </div>
  );
}

export function BackgroundPanel({ background, onSetBackground, imageAssets = [] }) {
  const [tab, setTab] = useState(background?.bgType === "gradient" ? "gradient" : background?.bgType === "image" ? "image" : "solid");
  const bg = background || {};
  const patch = (p) => onSetBackground({ ...bg, ...p });
  const tabs = [{ k: "solid", l: "Colour" }, { k: "gradient", l: "Gradient" }];
  if (isFeatureEnabled("background_image_enabled")) tabs.push({ k: "image", l: "Image" });
  if (isFeatureEnabled("admin_background_library_enabled")) tabs.push({ k: "library", l: "Library" });

  const [lib, setLib] = useState([]);
  const [cats, setCats] = useState([]);
  const [libCat, setLibCat] = useState("");
  const [libQ, setLibQ] = useState("");
  const [libType, setLibType] = useState("");
  useEffect(() => {
    if (tab !== "library") return;
    api.listBackgrounds({ category: libCat || undefined, q: libQ || undefined, media_type: libType || undefined })
      .then((d) => { setLib(d.backgrounds || []); setCats(d.categories || []); }).catch(() => {});
  }, [tab, libCat, libQ, libType]);

  const applyLibrary = (a) => {
    if (a.media_type === "video") {
      patch({ bgType: "video", asset_id: a.id, asset_url: a.storage_url, storage_path: a.storage_path, loop: true, source_start: 0, muted: true, fit: "cover" });
    } else {
      patch({ bgType: "image", asset_id: a.id, asset_url: a.storage_url, storage_path: a.storage_path, fit: bg.fit || "cover" });
    }
  };

  return (
    <div className="w-[300px] p-1" data-testid="background-panel">
      <div className="mb-3 flex gap-1 rounded-lg border border-white/10 bg-white/[0.02] p-0.5">
        {tabs.map((t) => (
          <button key={t.k} data-testid={`bg-tab-${t.k}`} onClick={() => setTab(t.k)}
            className={`flex-1 rounded-md py-1.5 text-xs font-semibold transition-colors ${tab === t.k ? "bg-[#00F0FF] text-black" : "text-[#A1A1AA] hover:text-white"}`}>{t.l}</button>
        ))}
      </div>

      {tab === "solid" && (
        <div>
          <div className="mb-2 grid grid-cols-5 gap-2">
            {SOLIDS.map((c) => (
              <button key={c} data-testid={`bg-solid-${c}`} onClick={() => patch({ bgType: "solid", color: c })}
                style={{ background: c }} className={`h-9 rounded-lg border ${bg.bgType === "solid" && bg.color === c ? "border-[#00F0FF]" : "border-white/15"}`} />
            ))}
          </div>
          <div className="flex items-center gap-2 rounded-lg border border-white/10 bg-white/[0.03] px-2 py-1.5">
            <input type="color" data-testid="bg-solid-picker" value={bg.color || "#000000"} onChange={(e) => patch({ bgType: "solid", color: e.target.value })} className="h-6 w-6 cursor-pointer rounded border-0 bg-transparent p-0" />
            <span className="font-plex text-xs uppercase text-white">{bg.color || "#000000"}</span>
          </div>
        </div>
      )}

      {tab === "gradient" && (
        <div className="grid grid-cols-2 gap-2">
          {GRADIENTS.map((g) => (
            <button key={g.label} data-testid={`bg-gradient-${g.label}`} onClick={() => patch({ bgType: "gradient", from: g.from, to: g.to, angle: bg.angle ?? 90 })}
              style={{ background: `linear-gradient(${bg.angle ?? 90}deg, ${g.from}, ${g.to})` }}
              className={`h-12 rounded-lg border text-[10px] font-bold text-white/90 ${bg.bgType === "gradient" && bg.from === g.from ? "border-[#00F0FF]" : "border-white/15"}`}>{g.label}</button>
          ))}
          {bg.bgType === "gradient" && (
            <div className="col-span-2 mt-1">
              <Field label="Angle" val={`${Math.round(bg.angle ?? 90)}°`}>
                <Slider data-testid="bg-gradient-angle" value={[bg.angle ?? 90]} min={0} max={360} step={5} onValueChange={([v]) => patch({ angle: v })} />
              </Field>
            </div>
          )}
        </div>
      )}

      {tab === "image" && (
        <div>
          {imageAssets.length === 0 && <p className="px-1 py-3 text-[11px] text-[#888]">Add an image to Project Media first, then pick it here as the background.</p>}
          <div className="grid grid-cols-3 gap-2">
            {imageAssets.map((a) => (
              <button key={a.id} data-testid={`bg-image-${a.id}`} onClick={() => patch({ bgType: "image", asset_id: a.id, asset_url: a.storage_url || a.url, fit: bg.fit || "cover" })}
                className={`aspect-square overflow-hidden rounded-lg border ${bg.asset_id === a.id ? "border-[#00F0FF]" : "border-white/15"}`}>
                <img src={absUrl(a.storage_url || a.url)} alt={a.name} className="h-full w-full object-cover" />
              </button>
            ))}
          </div>
          {bg.bgType === "image" && (
            <div className="mt-2 flex gap-1 rounded-lg border border-white/10 bg-white/[0.02] p-0.5">
              {["cover", "contain"].map((f) => (
                <button key={f} data-testid={`bg-image-fit-${f}`} onClick={() => patch({ fit: f })}
                  className={`flex-1 rounded-md py-1 text-xs font-semibold capitalize ${(bg.fit || "cover") === f ? "bg-white/15 text-white" : "text-[#A1A1AA]"}`}>{f}</button>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "library" && (
        <div data-testid="bg-library">
          <div className="mb-2 flex items-center gap-2 rounded-lg border border-white/10 bg-white/[0.03] px-2 py-1">
            <Search size={13} className="text-[#A1A1AA]" />
            <input data-testid="bg-lib-search" value={libQ} onChange={(e) => setLibQ(e.target.value)} placeholder="Search backgrounds…" className="w-full bg-transparent text-xs text-white outline-none" />
          </div>
          <div className="mb-2 flex flex-wrap gap-1">
            {[{ k: "", l: "All" }, { k: "image", l: "Images" }, { k: "video", l: "Videos" }].map((t) => (
              <button key={t.l} data-testid={`bg-lib-type-${t.l}`} onClick={() => setLibType(t.k)}
                className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${libType === t.k ? "bg-[#00F0FF] text-black" : "border border-white/15 text-[#A1A1AA]"}`}>{t.l}</button>
            ))}
            {cats.map((c) => (
              <button key={c} data-testid={`bg-lib-cat-${c}`} onClick={() => setLibCat(libCat === c ? "" : c)}
                className={`rounded-full px-2 py-0.5 text-[10px] font-semibold capitalize ${libCat === c ? "bg-[#8B5CF6] text-white" : "border border-white/15 text-[#A1A1AA]"}`}>{c}</button>
            ))}
          </div>
          {lib.length === 0 ? <p className="px-1 py-3 text-[11px] text-[#888]">No backgrounds yet. An admin can add stadium / news / cinematic backgrounds in Admin → Background Library.</p> : (
            <div className="grid max-h-[280px] grid-cols-3 gap-2 overflow-y-auto">
              {lib.map((a) => (
                <button key={a.id} data-testid={`bg-lib-item-${a.id}`} onClick={() => applyLibrary(a)} title={a.name}
                  className={`relative aspect-square overflow-hidden rounded-lg border ${bg.asset_id === a.id ? "border-[#00F0FF]" : "border-white/15"}`}>
                  <img src={absUrl(a.thumbnail_url || a.storage_url)} alt={a.name} className="h-full w-full object-cover" />
                  {a.media_type === "video" && <span className="absolute bottom-1 right-1 rounded bg-black/70 px-1 text-[8px] font-bold text-white">VIDEO</span>}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {bg.bgType === "video" && (
        <div className="mt-2 rounded-lg border border-white/10 bg-white/[0.02] p-2">
          <label className="flex items-center justify-between text-[11px] text-[#A1A1AA]">Loop
            <input type="checkbox" data-testid="bg-video-loop" checked={bg.loop !== false} onChange={(e) => patch({ loop: e.target.checked })} /></label>
          <Field label="Start offset" val={`${(bg.source_start || 0).toFixed(1)}s`}>
            <Slider data-testid="bg-video-offset" value={[bg.source_start || 0]} min={0} max={30} step={0.5} onValueChange={([v]) => patch({ source_start: v })} />
          </Field>
        </div>
      )}

      <div className="mt-3">
        <Field label="Opacity" val={`${Math.round((bg.opacity ?? 1) * 100)}%`}>
          <Slider data-testid="bg-opacity" value={[bg.opacity ?? 1]} min={0} max={1} step={0.05} onValueChange={([v]) => patch({ opacity: v })} />
        </Field>
      </div>
      {background && (
        <button data-testid="bg-remove" onClick={() => onSetBackground(null)} className="mt-2 w-full rounded-lg border border-white/15 py-2 text-xs font-semibold text-[#A1A1AA] hover:border-red-400/50 hover:text-red-400">Remove background</button>
      )}
    </div>
  );
}

export function VideoShapePanel({ clip, onPatch }) {
  if (!clip) return <div className="w-[300px] p-3 text-[11px] text-[#888]" data-testid="video-shape-panel">Select a video clip on the timeline to shape it.</div>;
  const s = { ...DEFAULT_VIDEO_SHAPE(), ...clip };
  const effect = s.effect || "none";
  const eInt = typeof s.effectIntensity === "number" ? s.effectIntensity : 60;
  return (
    <div className="w-[300px] p-1" data-testid="video-shape-panel">
      <div className="mb-1 text-[11px] font-bold uppercase tracking-wider text-[#A1A1AA]">Video Style</div>
      <div className="mb-2 grid grid-cols-3 gap-1.5" data-testid="video-style-select">
        {[["none", "None"], ["anime", "Anime"], ["cartoon", "Cartoon"]].map(([id, label]) => (
          <button key={id} data-testid={`video-style-${id}`} onClick={() => onPatch({ effect: id })}
            className={`rounded-lg border py-2 text-[10px] font-semibold transition-colors ${effect === id ? "border-[#30D158] bg-[#30D158]/15 text-white" : "border-white/10 bg-white/[0.02] text-[#A1A1AA] hover:border-white/25"}`}>{label}</button>
        ))}
      </div>
      {effect !== "none" && (
        <Field label="Style intensity" val={`${Math.round(eInt)}`}>
          <Slider data-testid="video-style-intensity" value={[eInt]} min={0} max={100} step={5}
            onValueChange={([v]) => onPatch({ effectIntensity: v })} />
        </Field>
      )}
      <p className="mb-3 text-[10px] text-[#666]">Native effect applied to the video only (captions stay crisp). Your source is never modified.</p>
      <div className="mb-1 text-[11px] font-bold uppercase tracking-wider text-[#A1A1AA]">Reframe on canvas</div>
      <div className="mb-3 grid grid-cols-3 gap-1.5" data-testid="reframe-helper">
        <button data-testid="reframe-fill" onClick={() => onPatch({ shape: "rect", shapeW: 1, shapeH: 1, shapeX: 0.5, shapeY: 0.5, mFit: "cover", mScale: 1, mX: 0, mY: 0, rotation: 0 })}
          className="rounded-lg border border-white/10 bg-white/[0.02] py-1.5 text-[11px] font-semibold text-white hover:border-white/25">Fill</button>
        <button data-testid="reframe-fit" onClick={() => onPatch({ shape: "rect", shapeW: 1, shapeH: 1, shapeX: 0.5, shapeY: 0.5, mFit: "contain", mScale: 1, mX: 0, mY: 0, rotation: 0 })}
          className="rounded-lg border border-white/10 bg-white/[0.02] py-1.5 text-[11px] font-semibold text-white hover:border-white/25">Fit</button>
        <button data-testid="reframe-centre" onClick={() => onPatch({ shapeX: 0.5, shapeY: 0.5, mX: 0, mY: 0 })}
          className="rounded-lg border border-white/10 bg-white/[0.02] py-1.5 text-[11px] font-semibold text-white hover:border-white/25">Centre</button>
      </div>
      <div className="mb-1 text-[11px] font-bold uppercase tracking-wider text-[#A1A1AA]">Shape</div>
      <div className="mb-3 grid grid-cols-3 gap-1.5">
        {SHAPE_TYPES.map((sh) => (
          <button key={sh.id} data-testid={`video-shape-${sh.id}`} onClick={() => onPatch({ shape: sh.id })}
            className={`rounded-lg border py-2 text-[10px] font-semibold transition-colors ${s.shape === sh.id ? "border-[#8B5CF6] bg-[#8B5CF6]/15 text-white" : "border-white/10 bg-white/[0.02] text-[#A1A1AA] hover:border-white/25"}`}>{sh.label}</button>
        ))}
      </div>
      <div className="mb-1 text-[11px] font-bold uppercase tracking-wider text-[#A1A1AA]">Video inside shape</div>
      <div className="mb-2 flex gap-1 rounded-lg border border-white/10 bg-white/[0.02] p-0.5">
        {["cover", "contain"].map((f) => (
          <button key={f} data-testid={`video-fit-${f}`} onClick={() => onPatch({ mFit: f })}
            className={`flex-1 rounded-md py-1 text-xs font-semibold capitalize ${(s.mFit || "cover") === f ? "bg-white/15 text-white" : "text-[#A1A1AA]"}`}>{f}</button>
        ))}
      </div>
      <Field label="Zoom" val={`${Math.round(s.mScale * 100)}%`}><Slider data-testid="video-zoom" value={[s.mScale]} min={0.5} max={3} step={0.05} onValueChange={([v]) => onPatch({ mScale: v })} /></Field>
      <Field label="Video X" val={s.mX.toFixed(2)}><Slider data-testid="video-mx" value={[s.mX]} min={-0.5} max={0.5} step={0.02} onValueChange={([v]) => onPatch({ mX: v })} /></Field>
      <Field label="Video Y" val={s.mY.toFixed(2)}><Slider data-testid="video-my" value={[s.mY]} min={-0.5} max={0.5} step={0.02} onValueChange={([v]) => onPatch({ mY: v })} /></Field>
      <div className="mb-1 mt-2 text-[11px] font-bold uppercase tracking-wider text-[#A1A1AA]">Shape size & position</div>
      <Field label="Width" val={`${Math.round(s.shapeW * 100)}%`}><Slider data-testid="shape-w" value={[s.shapeW]} min={0.1} max={1} step={0.02} onValueChange={([v]) => onPatch({ shapeW: v })} /></Field>
      <Field label="Height" val={`${Math.round(s.shapeH * 100)}%`}><Slider data-testid="shape-h" value={[s.shapeH]} min={0.1} max={1} step={0.02} onValueChange={([v]) => onPatch({ shapeH: v })} /></Field>
      <Field label="Position X" val={`${Math.round(s.shapeX * 100)}%`}><Slider data-testid="shape-x" value={[s.shapeX]} min={0} max={1} step={0.02} onValueChange={([v]) => onPatch({ shapeX: v })} /></Field>
      <Field label="Position Y" val={`${Math.round(s.shapeY * 100)}%`}><Slider data-testid="shape-y" value={[s.shapeY]} min={0} max={1} step={0.02} onValueChange={([v]) => onPatch({ shapeY: v })} /></Field>
      <Field label="Rotation" val={`${Math.round(s.rotation)}°`}><Slider data-testid="shape-rotation" value={[s.rotation]} min={-180} max={180} step={1} onValueChange={([v]) => onPatch({ rotation: v })} /></Field>
      <button data-testid="shape-reset" onClick={() => onPatch(DEFAULT_VIDEO_SHAPE())} className="mt-1 w-full rounded-lg border border-white/15 py-2 text-xs font-semibold text-[#A1A1AA] hover:text-white">Reset to full frame</button>
    </div>
  );
}
